import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import * as Tone from 'tone';
import './App.css';
import Landing from './components/Landing';
import ChopStation from './components/ChopStation';
import ProjectsPanel from './components/ProjectsPanel';
import DrumLoopEditor from './components/DrumLoopEditor';
import Mixer from './components/Mixer';
import SlideDeck from './components/SlideDeck';
import { saveProject, getProject } from './utils/projectStore';
import { createLoopBuffer } from './utils/loopBuffer';
import { audioBufferToWavBlob } from './utils/audioExport';
import { CHOP_KEYS } from './utils/colors';
import { smartChop, intervalChop } from './utils/transientDetection';
import { Agentation } from 'agentation';
import {
  storeFileHandle,
  getFileHandle,
  requestFileAccess,
  isFileSystemAccessSupported,
  openFilePicker,
  createHandleId
} from './utils/fileHandleStore';

const AVAILABLE_KEYS = CHOP_KEYS;

// Chop ends for one layer: an explicit end (set on pause / edge-drag) wins;
// otherwise a chop runs to the next chop's start, the last to the audio end.
const computeChopsWithEnds = (layer) => {
  if (!layer?.audioBuffer) return [];
  const sorted = [...layer.chops].sort((a, b) => a.start - b.start);
  return sorted.map((chop, index) => {
    const next = sorted[index + 1];
    const end = chop.end ?? (next ? next.start : layer.audioBuffer.duration);
    return { ...chop, end, isOpen: chop.end == null && !next };
  });
};

// Local YouTube sampling backend (see server/index.js)
const YT_BACKEND = process.env.REACT_APP_YT_BACKEND || 'http://localhost:5174';

// Bundled default drums, offered when a project has none yet
const DRUMS_URL = `${process.env.PUBLIC_URL || ''}/samples/gold-digger-drums.mp3`;

const MIXER_STORAGE_KEY = 'sampleband:mixer';
const MIXER_DB_MIN = -40; // bottom of the fader = mute
const dbToGain = (db) => (db <= MIXER_DB_MIN + 0.5 ? 0 : Math.pow(10, db / 20));

function App() {
  // Default ADSR values
  const DEFAULT_ADSR = {
    attack: 0,      // 0-1000ms
    decay: 0,       // 0-2000ms
    sustain: 100,   // 0-100%
    release: 131    // 0-3000ms
  };

  // Default playback mode settings (per-layer) — classic sampler feel on by default
  const DEFAULT_PLAYBACK_MODES = {
    gateEnabled: true,    // Hold-to-sustain behavior
    chokeEnabled: true,   // New note cuts previous note
    bleedEnabled: true,   // Ignore chop end boundary, play until release/choke/end-of-audio
    transpose: 0          // Semitones (-12 to +12)
  };

  // Default loop clip settings (per-layer)
  const DEFAULT_LOOP_CLIP = {
    isLoopClipEnabled: false,  // When true, loops the audio sample instead of playing recorded events
    loopClipStart: 0,          // Start trim point in seconds
    loopClipEnd: null          // End trim point (null = full duration)
  };

  // Factory for a fresh layer (source): audio + chops + per-layer settings
  const makeLayer = (id) => ({
    id,
    name: `Layer ${id}`,
    colorIndex: (id - 1) % 6,
    audioFile: null,
    audioBuffer: null,
    videoUrl: null,      // YouTube-sampled video URL for the visualizer
    youtubeTitle: null,  // Source video title
    chops: [],
    recordedEvents: [],
    isMuted: false,
    isSolo: false,
    loopMultiplier: 1,
    ...DEFAULT_ADSR,
    ...DEFAULT_PLAYBACK_MODES,
    ...DEFAULT_LOOP_CLIP
  });

  // Multi-layer system - each source is a layer; the focused layer receives
  // transport/chopping, but the keyboard triggers chops across ALL layers.
  const [layers, setLayers] = useState(() => [makeLayer(1)]);
  const [activeLayerId, setActiveLayerId] = useState(1);

  // Global state (not layer-specific)
  const [isLoopRecording, setIsLoopRecording] = useState(false);
  const [activePads, setActivePads] = useState(new Set());
  const [currentMode, setCurrentMode] = useState('chop');
  const [sequenceData, setSequenceData] = useState([]);
  const [loopLength, setLoopLength] = useState(4);
  const [recordedLoop, setRecordedLoop] = useState(null);
  const [loopDuration, setLoopDuration] = useState(0); // Shared across all layers
  const [isLoopPlaying, setIsLoopPlaying] = useState(false);
  const [loopCurrentTime, setLoopCurrentTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showEffects, setShowEffects] = useState(false);

  // View routing: 'main' (landing / chop station), 'loop-editor', or
  // 'slides' (the talk deck — open the app at /#talk)
  const [view, setView] = useState(() =>
    window.location.hash === '#talk' ? 'slides' : 'main'
  );

  // Active project (null = ad-hoc session). Changes auto-save to this project.
  const [activeProject, setActiveProject] = useState(null); // { id, name }

  // Backing drum loop (per session/project): drums audio + loop points
  const [drums, setDrums] = useState(null); // { blob, name, type }
  const [drumLoopPoints, setDrumLoopPoints] = useState(null);
  const [drumLoopPlaying, setDrumLoopPlaying] = useState(false);
  const drumPlayerRef = useRef(null);
  const drumsBufferRef = useRef(null); // decoded drums AudioBuffer
  const drumLoopStartedAtRef = useRef(0); // for the loop-source cell seeker

  // Mixer: master gain buses for the chop sampler and the drum loop
  const [mixerLevels, setMixerLevels] = useState(() => {
    try {
      return { chops: 0, drums: 0, ...JSON.parse(localStorage.getItem(MIXER_STORAGE_KEY)) };
    } catch {
      return { chops: 0, drums: 0 };
    }
  });
  const chopsGainRef = useRef(null);
  const drumsGainRef = useRef(null);
  const mixerLevelsRef = useRef(mixerLevels);

  const getChopsGain = useCallback(() => {
    if (!chopsGainRef.current) {
      chopsGainRef.current = new Tone.Gain(dbToGain(mixerLevelsRef.current.chops)).toDestination();
    }
    return chopsGainRef.current;
  }, []);

  const getDrumsGain = useCallback(() => {
    if (!drumsGainRef.current) {
      drumsGainRef.current = new Tone.Gain(dbToGain(mixerLevelsRef.current.drums)).toDestination();
    }
    return drumsGainRef.current;
  }, []);

  useEffect(() => {
    mixerLevelsRef.current = mixerLevels;
    try {
      localStorage.setItem(MIXER_STORAGE_KEY, JSON.stringify(mixerLevels));
    } catch { /* storage unavailable */ }
    if (chopsGainRef.current) chopsGainRef.current.gain.rampTo(dbToGain(mixerLevels.chops), 0.02);
    if (drumsGainRef.current) drumsGainRef.current.gain.rampTo(dbToGain(mixerLevels.drums), 0.02);
  }, [mixerLevels]);

  // Helper to get active layer
  const getActiveLayer = useCallback(() => {
    return layers.find(l => l.id === activeLayerId) || layers[0];
  }, [layers, activeLayerId]);

  // Helper to update active layer
  const updateActiveLayer = useCallback((updates) => {
    setLayers(prev => prev.map(layer =>
      layer.id === activeLayerId ? { ...layer, ...updates } : layer
    ));
  }, [activeLayerId]);

  const updateLayerById = useCallback((layerId, updates) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, ...updates } : layer
    ));
  }, []);

  // Extract active layer data for convenience
  const activeLayer = getActiveLayer();
  const audioBuffer = activeLayer.audioBuffer;
  const audioFile = activeLayer.audioFile;
  const chops = activeLayer.chops;
  const recordedEvents = activeLayer.recordedEvents;
  // Per-layer ADSR values
  const attack = activeLayer.attack ?? DEFAULT_ADSR.attack;
  const decay = activeLayer.decay ?? DEFAULT_ADSR.decay;
  const sustain = activeLayer.sustain ?? DEFAULT_ADSR.sustain;
  const release = activeLayer.release ?? DEFAULT_ADSR.release;

  // Per-layer playback mode values
  const gateEnabled = activeLayer.gateEnabled ?? DEFAULT_PLAYBACK_MODES.gateEnabled;
  const chokeEnabled = activeLayer.chokeEnabled ?? DEFAULT_PLAYBACK_MODES.chokeEnabled;
  const bleedEnabled = activeLayer.bleedEnabled ?? DEFAULT_PLAYBACK_MODES.bleedEnabled;
  const transpose = activeLayer.transpose ?? DEFAULT_PLAYBACK_MODES.transpose;

  // Per-layer loop clip values
  const isLoopClipEnabled = activeLayer.isLoopClipEnabled ?? DEFAULT_LOOP_CLIP.isLoopClipEnabled;
  const loopClipStart = activeLayer.loopClipStart ?? DEFAULT_LOOP_CLIP.loopClipStart;
  const loopClipEnd = activeLayer.loopClipEnd ?? (activeLayer.audioBuffer?.duration || null);

  // Keep refs in sync with per-layer values (for callbacks that can't access current state)
  const chokeEnabledRef = useRef(chokeEnabled);
  const gateEnabledRef = useRef(gateEnabled);
  const bleedEnabledRef = useRef(bleedEnabled);
  const transposeRef = useRef(transpose);
  const activeLayerIdRef = useRef(activeLayerId);

  useEffect(() => {
    chokeEnabledRef.current = chokeEnabled;
    gateEnabledRef.current = gateEnabled;
    bleedEnabledRef.current = bleedEnabled;
    transposeRef.current = transpose;
    activeLayerIdRef.current = activeLayerId;
  }, [chokeEnabled, gateEnabled, bleedEnabled, transpose, activeLayerId]);

  // Flash effect state - tracks which chops are currently flashing
  const [flashingChops, setFlashingChops] = useState(new Set());

  // ADSR setters that update the active layer
  const setAttack = useCallback((value) => updateActiveLayer({ attack: value }), [updateActiveLayer]);
  const setDecay = useCallback((value) => updateActiveLayer({ decay: value }), [updateActiveLayer]);
  const setSustain = useCallback((value) => updateActiveLayer({ sustain: value }), [updateActiveLayer]);
  const setRelease = useCallback((value) => updateActiveLayer({ release: value }), [updateActiveLayer]);

  // Playback mode setters that update the active layer
  const setGateEnabled = useCallback((value) => updateActiveLayer({ gateEnabled: value }), [updateActiveLayer]);
  const setChokeEnabled = useCallback((value) => updateActiveLayer({ chokeEnabled: value }), [updateActiveLayer]);
  const setBleedEnabled = useCallback((value) => updateActiveLayer({ bleedEnabled: value }), [updateActiveLayer]);
  const setTranspose = useCallback((value) => updateActiveLayer({ transpose: value }), [updateActiveLayer]);

  // Loop clip setters
  const setIsLoopClipEnabled = useCallback((value) => {
    updateActiveLayer({ isLoopClipEnabled: value });

    // If enabling loop clip and there's no master duration yet, set it from the audio clip
    if (value && loopDuration === 0 && audioBuffer) {
      const clipStart = activeLayer.loopClipStart ?? 0;
      const clipEnd = activeLayer.loopClipEnd ?? audioBuffer.duration;
      const clipDuration = clipEnd - clipStart;
      setLoopDuration(clipDuration);
      console.log('[App] Set master loop duration from loop clip:', clipDuration);
    }
  }, [updateActiveLayer, loopDuration, audioBuffer, activeLayer]);
  const setLoopClipStart = useCallback((value) => updateActiveLayer({ loopClipStart: value }), [updateActiveLayer]);
  const setLoopClipEnd = useCallback((value) => updateActiveLayer({ loopClipEnd: value }), [updateActiveLayer]);

  const recordingStartTimeRef = useRef(null);
  const recorderRef = useRef(null);
  const wavesurferRef = useRef(null);
  const activePlayersRef = useRef([]); // Track active players for choke mode
  const activeEnvelopesRef = useRef({}); // Track active envelopes by key for release on keyup
  const loopStartTimeRef = useRef(null); // Timestamp when spacebar was pressed
  const loopPlayerRef = useRef(null); // Tone.js Part for loop playback
  const recordedEventsRef = useRef([]); // Use ref to avoid stale closure issues
  const preloadedPlayersRef = useRef({}); // Pre-loaded players for instant playback
  const loopActivePlayersRef = useRef({}); // Track current playing player per layer during loop playback
  const activeRecordingKeysRef = useRef({}); // Track active key presses during recording for duration
  const playerIdCounter = useRef(0); // Debug counter for tracking players
  const videoVisualizerRef = useRef(null); // Imperative handle to seek the video visualizer
  const chopPlaybackRef = useRef({}); // key -> { startedAt, rate } for the chop-lane mini seeker

  // Handle file upload - accepts file and optional file handle for persistence.
  // extraLayerData lets callers (e.g. YouTube sampling) attach fields like videoUrl.
  // targetLayerId directs the audio into a specific layer (default: focused).
  const handleFileUpload = async (file, fileHandle = null, extraLayerData = {}, targetLayerId = null) => {
    const layerId = targetLayerId ?? activeLayerIdRef.current;
    console.log('[App] handleFileUpload called with file:', file.name, file.type, file.size, '→ layer', layerId);

    try {
      // Start Tone with lowest latency settings
      await Tone.start();

      // Set context for minimal latency
      Tone.context.lookAhead = 0;
      console.log('[App] Tone.js context started with minimal lookAhead for instant playback');

      const arrayBuffer = await file.arrayBuffer();
      console.log('[App] ArrayBuffer created, size:', arrayBuffer.byteLength);

      const buffer = await Tone.context.decodeAudioData(arrayBuffer);
      console.log('[App] Audio decoded, duration:', buffer.duration, 'sampleRate:', buffer.sampleRate);

      // Store file handle if provided (for later reload)
      let fileHandleId = null;
      if (fileHandle) {
        fileHandleId = createHandleId(layerId, file.name);
        await storeFileHandle(fileHandleId, fileHandle, {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          layerId
        });
        console.log('[App] Stored file handle:', fileHandleId);
      }

      // Update the target layer with new audio and handle reference.
      // Clear any prior YouTube video unless the caller supplies a new one.
      updateLayerById(layerId, {
        audioFile: file,
        audioBuffer: buffer,
        chops: [],
        videoUrl: null,
        youtubeTitle: null,
        fileHandleId, // Store reference to the handle in IndexedDB
        ...extraLayerData
      });

      console.log('[App] Layer', layerId, 'updated with new audio, chops cleared');
    } catch (error) {
      console.error('[App] Error uploading file:', error);
    }
  };

  // Sample a YouTube URL: backend downloads it, we load the audio into the
  // active layer and wire the video to the visualizer. Throws on failure so the
  // panel can surface the error.
  const handleYouTubeSample = useCallback(async (url, targetLayerId = null) => {
    const res = await fetch(`${YT_BACKEND}/api/sample`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url })
    });

    if (!res.ok) {
      let message = 'Backend error. Is the sampling server running?';
      try {
        const data = await res.json();
        if (data.error) message = data.error;
      } catch { /* non-JSON error body */ }
      throw new Error(message);
    }

    const { audioUrl, videoUrl, title } = await res.json();

    // Fetch the extracted audio and run it through the normal decode pipeline.
    const audioRes = await fetch(`${YT_BACKEND}${audioUrl}`);
    if (!audioRes.ok) throw new Error('Failed to download sampled audio.');
    const blob = await audioRes.blob();
    const safeName = (title || 'youtube-sample').replace(/[^a-z0-9]+/gi, '_').slice(0, 60);
    const file = new File([blob], `${safeName}.mp3`, { type: 'audio/mpeg' });

    await handleFileUpload(file, null, {
      videoUrl: `${YT_BACKEND}${videoUrl}`,
      youtubeTitle: title
    }, targetLayerId);
  }, []);

  // Open file picker using File System Access API (for persistent handles)
  const handleOpenFilePicker = async () => {
    if (!isFileSystemAccessSupported()) {
      // Fallback to traditional file input
      console.log('[App] File System Access API not supported, using fallback');
      return false;
    }

    try {
      const handles = await openFilePicker({ multiple: false });
      if (!handles || handles.length === 0) return false;

      const handle = handles[0];
      const file = await handle.getFile();

      await handleFileUpload(file, handle);
      return true;
    } catch (error) {
      console.error('[App] Error opening file picker:', error);
      return false;
    }
  };

  // Handle rendered stretched audio (from CHOP SAMPLE button)
  const handleRenderStretchedAudio = useCallback((newAudioBuffer, newAudioFile) => {
    console.log('[App] Replacing audio with rendered stretched version:', newAudioBuffer.duration, 'seconds');

    // Update active layer: replace audio, clear chops, disable loop clip
    updateActiveLayer({
      audioFile: newAudioFile,
      audioBuffer: newAudioBuffer,
      chops: [], // Clear existing chops
      isLoopClipEnabled: false, // Turn off loop clip mode
      loopClipStart: 0,
      loopClipEnd: null
    });

    console.log('[App] Layer updated with stretched audio, chops cleared, loop clip disabled');
  }, [updateActiveLayer]);

  // Chop ends for the focused layer (recording engine still uses this)
  const getChopsWithEnds = useCallback(() => {
    return computeChopsWithEnds(getActiveLayer());
  }, [getActiveLayer]);

  // key -> owning layer id, across all layers (shared keyboard)
  const chopKeyOwnersRef = useRef({});
  useEffect(() => {
    const owners = {};
    for (const layer of layers) {
      for (const chop of layer.chops) owners[chop.key] = layer.id;
    }
    chopKeyOwnersRef.current = owners;
  }, [layers]);

  // When source playback pauses, close out the still-open last chop at the
  // pause position (the final chop runs from its start until the user stops).
  const finalizeOpenChop = useCallback((time) => {
    const currentChops = getActiveLayer().chops;
    if (currentChops.length === 0) return;

    const last = [...currentChops].sort((a, b) => a.start - b.start)[currentChops.length - 1];
    if (last.end == null && time > last.start + 0.02) {
      updateActiveLayer({
        chops: currentChops.map(c => (c.id === last.id ? { ...c, end: time } : c))
      });
    }
  }, [getActiveLayer, updateActiveLayer]);

  // --- Backing drum loop -------------------------------------------------
  const drumsUrl = useMemo(
    () => (drums ? URL.createObjectURL(drums.blob) : null),
    [drums]
  );
  useEffect(() => {
    return () => { if (drumsUrl) URL.revokeObjectURL(drumsUrl); };
  }, [drumsUrl]);

  // New drums invalidate the decoded buffer and any running player
  useEffect(() => {
    drumsBufferRef.current = null;
    return () => {
      if (drumPlayerRef.current) {
        try { drumPlayerRef.current.stop(); } catch { /* not started */ }
        drumPlayerRef.current.dispose();
        drumPlayerRef.current = null;
      }
    };
  }, [drums]);

  const stopDrumLoop = useCallback(() => {
    if (drumPlayerRef.current) {
      try { drumPlayerRef.current.stop(); } catch { /* not started */ }
    }
    setDrumLoopPlaying(false);
  }, []);

  const startDrumLoop = useCallback(async (points) => {
    if (!drums) return;
    await Tone.start();
    if (!drumsBufferRef.current) {
      const ab = await drums.blob.arrayBuffer();
      drumsBufferRef.current = await Tone.context.decodeAudioData(ab);
    }
    if (drumPlayerRef.current) {
      try { drumPlayerRef.current.stop(); } catch { /* not started */ }
      drumPlayerRef.current.dispose();
    }
    // The loop buffer is the selected segment with micro-fades baked in at
    // both ends, so the wrap point can't click.
    const loopBuffer = createLoopBuffer(drumsBufferRef.current, points?.start, points?.end);
    const player = new Tone.Player(loopBuffer).connect(getDrumsGain());
    player.loop = true;
    player.start();
    drumPlayerRef.current = player;
    drumLoopStartedAtRef.current = performance.now();
    setDrumLoopPlaying(true);
  }, [drums, getDrumsGain]);

  const toggleDrumLoop = useCallback(() => {
    if (drumLoopPlaying) stopDrumLoop();
    else startDrumLoop(drumLoopPoints);
  }, [drumLoopPlaying, drumLoopPoints, startDrumLoop, stopDrumLoop]);

  const saveDrumLoopPoints = useCallback((points) => {
    setDrumLoopPoints(points);
    if (drumLoopPlaying) startDrumLoop(points); // apply live
    setView('main');
  }, [drumLoopPlaying, startDrumLoop]);

  const handleAddDrums = useCallback((file) => {
    stopDrumLoop();
    setDrums({ blob: file, name: file.name, type: file.type });
    setDrumLoopPoints(null);
  }, [stopDrumLoop]);

  // Right-click a lane chop → "loop": that chop's segment (rendered to a
  // gapless WAV) becomes the project's drums file and starts looping.
  const autoStartDrumsRef = useRef(false);
  useEffect(() => {
    if (autoStartDrumsRef.current && drums) {
      autoStartDrumsRef.current = false;
      startDrumLoop(null);
    }
  }, [drums, startDrumLoop]);

  const handleLoopChopAsDrums = useCallback((chop) => {
    const pre = preloadedPlayersRef.current[chop.id];
    const layer = layers.find(l => l.id === pre?.layerId);
    if (!layer?.audioBuffer) return;
    const end = chop.end ?? (chop.start + (pre?.duration || 0.5));
    const blob = audioBufferToWavBlob(layer.audioBuffer, chop.start, end);
    stopDrumLoop();
    autoStartDrumsRef.current = true;
    // sourceChopId links the drums back to the originating lane chop so it
    // can light up and show the loop seeker while the drums play.
    setDrums({
      blob,
      name: `${chop.key.toLowerCase()}-chop-loop.wav`,
      type: 'audio/wav',
      sourceChopId: chop.id,
      sourceChopStart: chop.start
    });
    setDrumLoopPoints(null);
  }, [layers, stopDrumLoop]);

  const handleUseDefaultDrums = useCallback(async () => {
    try {
      const res = await fetch(DRUMS_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      stopDrumLoop();
      setDrums({ blob, name: 'gold-digger-drums.mp3', type: 'audio/mpeg' });
      setDrumLoopPoints(null);
    } catch (e) {
      console.error('[App] failed to load bundled drums:', e);
    }
  }, [stopDrumLoop]);

  // --- Additional sources (multi-sample chopping) -------------------------
  // "add audio file": a new layer becomes the focused source; the keyboard
  // is shared across all sources.
  const createSourceLayer = useCallback(() => {
    const newId = Math.max(...layers.map(l => l.id), 0) + 1;
    setLayers(prev => [...prev, makeLayer(newId)]);
    setActiveLayerId(newId);
    return newId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  const handleAddSourceFile = useCallback(async (file) => {
    const newId = createSourceLayer();
    await handleFileUpload(file, null, {}, newId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSourceLayer]);

  const handleAddSourceUrl = useCallback(async (url) => {
    const newId = createSourceLayer();
    try {
      await handleYouTubeSample(url, newId);
    } catch (e) {
      // Sampling failed: drop the empty layer again
      setLayers(prev => prev.filter(l => l.id !== newId));
      setActiveLayerId(activeLayerIdRef.current === newId ? layers[0]?.id ?? 1 : activeLayerIdRef.current);
      throw e;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSourceLayer, handleYouTubeSample, layers]);

  // --- Project system: live documents with auto-save ---------------------
  const resetSession = useCallback(() => {
    stopDrumLoop();
    setLayers([makeLayer(1)]);
    setActiveLayerId(1);
    setDrums(null);
    setDrumLoopPoints(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopDrumLoop]);

  const buildProjectRecord = useCallback((id, name) => {
    return {
      id,
      name,
      layers: layers
        .filter(l => l.audioFile)
        .map(l => ({
          audioBlob: l.audioFile,
          audioName: l.audioFile.name,
          audioType: l.audioFile.type,
          chops: l.chops,
          videoUrl: l.videoUrl || null,
          youtubeTitle: l.youtubeTitle || null
        })),
      drumsBlob: drums?.blob || null,
      drumsName: drums?.name || null,
      drumsType: drums?.type || null,
      drumsSourceChopId: drums?.sourceChopId ?? null,
      drumsSourceChopStart: drums?.sourceChopStart ?? null,
      drumLoop: drumLoopPoints || null
    };
  }, [layers, drums, drumLoopPoints]);

  const handleNewProject = useCallback(async (name) => {
    const id = `p_${Date.now()}`;
    resetSession();
    setActiveProject({ id, name });
    await saveProject({ id, name, chops: [], audioBlob: null, drumsBlob: null, drumLoop: null });
  }, [resetSession]);

  const handleSaveCurrentProject = useCallback(async (name) => {
    const id = `p_${Date.now()}`;
    await saveProject(buildProjectRecord(id, name));
    setActiveProject({ id, name });
  }, [buildProjectRecord]);

  const handleLoadSavedProject = useCallback(async (id) => {
    const p = await getProject(id);
    if (!p) return;
    stopDrumLoop();

    // Multi-source records store a layers array; legacy records stored a
    // single layer's fields at the top level.
    const layerDatas = p.layers || (p.audioBlob
      ? [{
          audioBlob: p.audioBlob,
          audioName: p.audioName,
          audioType: p.audioType,
          chops: p.chops,
          videoUrl: p.videoUrl,
          youtubeTitle: p.youtubeTitle
        }]
      : []);

    if (layerDatas.length === 0) {
      setLayers([makeLayer(1)]);
      setActiveLayerId(1);
    } else {
      await Tone.start();
      const newLayers = [];
      for (let i = 0; i < layerDatas.length; i++) {
        const ld = layerDatas[i];
        const file = new File([ld.audioBlob], ld.audioName || `${p.name}-${i + 1}.mp3`, {
          type: ld.audioType || 'audio/mpeg'
        });
        const audioBuffer = await Tone.context.decodeAudioData(await file.arrayBuffer());
        newLayers.push({
          ...makeLayer(i + 1),
          audioFile: file,
          audioBuffer,
          chops: ld.chops || [],
          videoUrl: ld.videoUrl || null,
          youtubeTitle: ld.youtubeTitle || null
        });
      }
      setLayers(newLayers);
      setActiveLayerId(newLayers[0].id);
    }

    setDrums(p.drumsBlob
      ? {
          blob: p.drumsBlob,
          name: p.drumsName || 'drums',
          type: p.drumsType || 'audio/mpeg',
          sourceChopId: p.drumsSourceChopId ?? null,
          sourceChopStart: p.drumsSourceChopStart ?? null
        }
      : null);
    setDrumLoopPoints(p.drumLoop || null);
    setActiveProject({ id: p.id, name: p.name });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopDrumLoop]);

  const handleProjectDeleted = useCallback((id) => {
    setActiveProject((cur) => (cur && cur.id === id ? null : cur));
  }, []);

  // Auto-save the open project whenever its contents change (debounced)
  useEffect(() => {
    if (!activeProject) return;
    const t = setTimeout(() => {
      saveProject(buildProjectRecord(activeProject.id, activeProject.name))
        .catch((e) => console.error('[App] autosave failed:', e));
    }, 600);
    return () => clearTimeout(t);
  }, [activeProject, buildProjectRecord]);

  // Add or replace a chop at current playback time
  const addChopAtTime = useCallback((key, time) => {
    console.log('[App] addChopAtTime:', key, 'at', time);

    updateActiveLayer({
      chops: (() => {
        const currentChops = getActiveLayer().chops;
        // Remove existing chop with this key if it exists
        const filtered = currentChops.filter(c => c.key !== key);

        // Add new chop at current time
        const newChop = {
          id: Date.now(),
          key,
          start: time
        };

        console.log('[App] Created/replaced chop:', newChop);
        return [...filtered, newChop];
      })()
    });
  }, [updateActiveLayer, getActiveLayer]);

  // Update chop start time (when dragging region on waveform)
  // Chop edits address chops by id across ALL layers (the lane mixes sources)
  const patchChopById = useCallback((id, patchFn) => {
    setLayers(prev => prev.map(layer =>
      layer.chops.some(c => c.id === id)
        ? { ...layer, chops: patchFn(layer.chops) }
        : layer
    ));
  }, []);

  const updateChopStart = useCallback((id, newStart) => {
    console.log('[App] updateChopStart:', id, 'to', newStart);
    patchChopById(id, (chops) => chops.map(c => (c.id === id ? { ...c, start: newStart } : c)));
  }, [patchChopById]);

  // Set an explicit end (wins over the next-chop-derived end)
  const updateChopEnd = useCallback((id, newEnd) => {
    console.log('[App] updateChopEnd:', id, 'to', newEnd);
    patchChopById(id, (chops) => chops.map(c => (c.id === id ? { ...c, end: newEnd } : c)));
  }, [patchChopById]);

  const removeChop = useCallback((id) => {
    console.log('[App] removeChop:', id);
    patchChopById(id, (chops) => chops.filter(c => c.id !== id));
  }, [patchChopById]);

  // Chop timing lookup across ALL layers: chop id -> { start, duration,
  // layerId }. (No Tone.Player instances needed here — triggerSample builds
  // its own player from the layer's buffer at trigger time.)
  useEffect(() => {
    const map = {};
    for (const layer of layers) {
      if (!layer.audioBuffer) continue;
      for (const chop of computeChopsWithEnds(layer)) {
        map[chop.id] = {
          start: chop.start,
          duration: chop.end - chop.start,
          layerId: layer.id
        };
      }
    }
    // Dispose players from the legacy preload shape, if any linger
    Object.values(preloadedPlayersRef.current).forEach((entry) => entry?.player?.dispose?.());
    preloadedPlayersRef.current = map;
  }, [layers]);

  // Trigger flash effect for a chop (200ms duration with ease-out)
  const triggerFlash = useCallback((chopKey) => {
    setFlashingChops(prev => new Set(prev).add(chopKey));
    setTimeout(() => {
      setFlashingChops(prev => {
        const newSet = new Set(prev);
        newSet.delete(chopKey);
        return newSet;
      });
    }, 200); // 200ms flash duration
  }, []);

  /**
   * UNIFIED SAMPLE TRIGGER FUNCTION
   *
   * This is the single source of truth for playing audio samples.
   * Both live playback (key presses) and loop playback (sequencer) use this.
   *
   * @param {Object} options
   * @param {AudioBuffer} options.buffer - The audio buffer to play from
   * @param {number} options.startOffset - Start position in the buffer (seconds)
   * @param {number} options.duration - How long to play (seconds) - ignored if bleed is true
   * @param {number} options.bufferDuration - Total duration of the audio buffer (for bleed mode)
   * @param {number} options.time - When to start (Tone.now() for live, scheduled for sequencer)
   * @param {Object} options.adsr - ADSR settings { attack, decay, sustain, release } in ms/%
   * @param {boolean} options.useGate - Whether to use full ADSR (true) or just micro-fades (false)
   * @param {boolean} options.isHeld - For gate mode: if true, waits for manual release
   * @param {boolean} options.bleed - If true, ignore chop end boundary, play until release/choke/end-of-audio
   * @param {number} options.playbackRate - Playback rate (1 = normal, 2 = octave up, 0.5 = octave down)
   * @param {Function} options.onCleanup - Optional callback when playback ends
   * @returns {Object} - Reference for stopping/releasing { player, envelope, stop(), release() }
   */
  const triggerSample = useCallback(({
    buffer,
    startOffset,
    duration,
    bufferDuration,
    time = Tone.now(),
    adsr = DEFAULT_ADSR,
    useGate = false,
    isHeld = false,
    bleed = false,
    playbackRate = 1,
    onCleanup = null
  }) => {
    // Micro-fade time to prevent clicks (in seconds)
    const MICRO_FADE = 0.005; // 5ms

    // Generate unique ID for this player (for debugging)
    const playerId = ++playerIdCounter.current;

    // Calculate effective duration based on bleed mode
    // BLEED: play from start offset to end of audio buffer
    // NORMAL: play the defined chop duration
    const effectiveDuration = bleed
      ? (bufferDuration || buffer.duration) - startOffset
      : duration;

    // Adjust duration for playback rate (faster playback = shorter duration)
    const adjustedDuration = effectiveDuration / playbackRate;

    console.log(`[triggerSample #${playerId}] Creating player - bleed: ${bleed}, effectiveDuration: ${effectiveDuration.toFixed(2)}s, playbackRate: ${playbackRate.toFixed(2)}, activeCount: ${activePlayersRef.current.length}`);

    // Create player with playback rate for transpose
    const player = new Tone.Player({
      url: buffer,
      loop: false,
      playbackRate: playbackRate
    });

    let envelope;
    let maxPlayTime;

    if (useGate && isHeld) {
      // GATE MODE: Full ADSR envelope, waits for manual release
      // With BLEED: plays until key release (or end of audio)
      const effectiveAttack = Math.max((adsr.attack || 0) / 1000, MICRO_FADE);
      const effectiveRelease = Math.max((adsr.release || 131) / 1000, MICRO_FADE);

      envelope = new Tone.AmplitudeEnvelope({
        attack: effectiveAttack,
        decay: (adsr.decay || 0) / 1000,
        sustain: (adsr.sustain ?? 100) / 100,
        release: effectiveRelease
      }).connect(getChopsGain());

      player.connect(envelope);
      player.start(time, startOffset, effectiveDuration);
      envelope.triggerAttack(time);

      // Failsafe max play time (in case release is never called)
      // Adjust for playback rate (faster playback = shorter wall-clock time)
      maxPlayTime = adjustedDuration + Math.max(effectiveRelease, 0.5);
      player.stop(time + maxPlayTime);

    } else {
      // NON-GATE MODE (loop playback, quick taps): No ADSR, just 5ms fade-out at end
      envelope = new Tone.AmplitudeEnvelope({
        attack: 0,
        decay: 0,
        sustain: 1,
        release: MICRO_FADE
      }).connect(getChopsGain());

      player.connect(envelope);
      player.start(time, startOffset, effectiveDuration);
      envelope.triggerAttack(time);

      // Trigger release just before end so 5ms fade completes naturally
      // Use adjustedDuration for wall-clock timing
      const releaseTime = time + adjustedDuration - MICRO_FADE;
      envelope.triggerRelease(releaseTime > time ? releaseTime : time);

      // Stop after duration + fade
      maxPlayTime = adjustedDuration + MICRO_FADE;
      player.stop(time + maxPlayTime);

      console.log(`[triggerSample #${playerId}] NON-GATE: scheduled stop at ${(time + maxPlayTime).toFixed(2)}, current Tone.now(): ${Tone.now().toFixed(2)}`);
    }

    // Cleanup side effects (pad unhighlight, seeker removal, video stop) must
    // run exactly once no matter how playback ends — stop/choke, gate
    // release, or the scheduled timer. stop() previously skipped onCleanup
    // entirely, leaving choked chops highlighted forever.
    let cleanupDone = false;
    const runCleanup = () => {
      if (cleanupDone) return;
      cleanupDone = true;
      if (onCleanup) onCleanup();
    };

    // Create reference object for external control
    const ref = {
      player,
      envelope,
      duration,
      startTime: time,

      // Stop with quick fade-out (for choke mode and pause)
      stop: () => {
        try {
          console.log(`[triggerSample #${playerId}] stop() called - player state: ${player.state}`);
          // Quick fade-out to prevent clicks (3ms)
          const fadeTime = 0.003;
          envelope.triggerRelease(Tone.now());
          player.stop(Tone.now() + fadeTime + 0.001);

          // Cleanup after fade
          setTimeout(() => {
            try {
              player.dispose();
              envelope.dispose();
              console.log(`[triggerSample #${playerId}] disposed after stop()`);
            } catch (e) {
              console.log(`[triggerSample #${playerId}] dispose error after stop():`, e.message);
            }
            runCleanup();
          }, 50);
        } catch (e) {
          console.log(`[triggerSample #${playerId}] stop() error:`, e.message);
          runCleanup();
        }
      },

      // Trigger release phase (for gate mode key-up)
      release: (releaseTime = Tone.now()) => {
        console.log(`[triggerSample #${playerId}] release() called - useGate: ${useGate}, isHeld: ${isHeld}, player state: ${player.state}`);

        if (!useGate || !isHeld) {
          console.log(`[triggerSample #${playerId}] release() SKIPPED - useGate=${useGate}, isHeld=${isHeld}`);
          return;
        }

        try {
          const effectiveRelease = Math.max((adsr.release || 131) / 1000, MICRO_FADE);
          console.log(`[triggerSample #${playerId}] release() scheduling stop at ${(releaseTime + effectiveRelease).toFixed(2)}, current: ${Tone.now().toFixed(2)}, release time: ${effectiveRelease.toFixed(3)}s`);
          envelope.triggerRelease(releaseTime);
          player.stop(releaseTime + effectiveRelease);

          // Cleanup after release
          setTimeout(() => {
            try {
              player.dispose();
              envelope.dispose();
            } catch (e) {}
            runCleanup();
          }, effectiveRelease * 1000 + 100);
        } catch (e) {
          console.warn('[triggerSample] Error during release:', e);
        }
      },

      // Schedule cleanup (for non-held playback)
      scheduleCleanup: () => {
        const cleanupDelay = (maxPlayTime * 1000) + 100;
        console.log(`[triggerSample #${playerId}] scheduling cleanup in ${cleanupDelay.toFixed(0)}ms`);
        setTimeout(() => {
          try {
            console.log(`[triggerSample #${playerId}] cleanup firing - player state: ${player.state}`);
            player.stop();
            player.dispose();
            envelope.dispose();
            console.log(`[triggerSample #${playerId}] cleanup complete`);
          } catch (e) {
            console.log(`[triggerSample #${playerId}] cleanup error:`, e.message);
          }
          runCleanup();
        }, cleanupDelay);
      }
    };

    // Auto-schedule cleanup for non-held (one-shot) playback
    if (!useGate || !isHeld) {
      ref.scheduleCleanup();
    }

    return ref;
  }, []);

  // FULLY SYNCHRONOUS - no async/await to prevent race conditions with rapid key presses
  // opts.bleedOverride forces bleed on/off for this trigger (mouse clicks
  // preview the chop bounded, since a click has no "release").
  // The chop's OWNING layer supplies buffer + gate/choke/bleed/transpose/ADSR,
  // so the shared keyboard plays every source with its own settings.
  const playChop = (chop, isKeyHold = false, opts = {}) => {
    const preEntry = preloadedPlayersRef.current[chop.id];
    const layer = layers.find(l => l.id === preEntry?.layerId) || getActiveLayer();
    const buffer = layer?.audioBuffer;
    if (!buffer) return;

    const layerGate = layer.gateEnabled ?? DEFAULT_PLAYBACK_MODES.gateEnabled;
    const layerChoke = layer.chokeEnabled ?? DEFAULT_PLAYBACK_MODES.chokeEnabled;
    const layerBleed = layer.bleedEnabled ?? DEFAULT_PLAYBACK_MODES.bleedEnabled;
    const layerTranspose = layer.transpose ?? DEFAULT_PLAYBACK_MODES.transpose;
    const layerAdsr = {
      attack: layer.attack ?? DEFAULT_ADSR.attack,
      decay: layer.decay ?? DEFAULT_ADSR.decay,
      sustain: layer.sustain ?? DEFAULT_ADSR.sustain,
      release: layer.release ?? DEFAULT_ADSR.release
    };

    const useGate = layerGate && isKeyHold;
    const useBleed = opts.bleedOverride ?? layerBleed;

    console.log(`[playChop] ENTER - key=${chop.key}, layer=${layer.id}, useGate=${useGate}, useBleed=${useBleed}, isKeyHold=${isKeyHold}`);

    // Stop any existing player for THIS KEY
    if (useGate && activeEnvelopesRef.current[chop.key]) {
      console.log(`[playChop] Stopping existing player for key: ${chop.key}`);
      activeEnvelopesRef.current[chop.key].stop();
      delete activeEnvelopesRef.current[chop.key];
    }

    // CHOKE: stop all players
    if (layerChoke) {
      activePlayersRef.current.forEach(ref => ref.stop());
      activePlayersRef.current = [];
      Object.values(activeEnvelopesRef.current).forEach(ref => ref.stop());
      activeEnvelopesRef.current = {};
    }

    // Start audio context (fire-and-forget, Tone.js queues audio until ready)
    Tone.start();

    try {

      // Trigger visual flash effect
      triggerFlash(chop.key);

      // Get pre-loaded timing info
      const preloaded = preEntry;
      if (!preloaded) {
        console.warn('[App] No pre-loaded timing for chop:', chop.id);
        return;
      }

      const { start, duration } = preloaded;

      // Calculate playback rate from transpose (semitones)
      // +12 semitones = 2x rate (octave up), -12 semitones = 0.5x rate (octave down)
      const transposeRate = Math.pow(2, layerTranspose / 12);

      // How long this trigger will actually sound (bleed runs past the chop end)
      const effectiveDuration = useBleed ? buffer.duration - start : duration;

      // Seek the owning source's video to this chop's frame. Keep the
      // returned sequence token so this trigger's cleanup can stop the
      // video when its audio stops (choke/release), staying in sync.
      let segSeq = null;
      if (videoVisualizerRef.current) {
        segSeq = videoVisualizerRef.current.playSegment(layer.id, start, effectiveDuration, transposeRate);
      }

      // Track playback for the chop-lane mini seeker
      const playEntry = { startedAt: performance.now(), rate: transposeRate };
      chopPlaybackRef.current[chop.key] = playEntry;

      // Use unified trigger function
      const sampleRef = triggerSample({
        buffer,
        startOffset: start,
        duration: duration,
        bufferDuration: buffer.duration, // For BLEED mode
        time: Tone.now(),
        adsr: layerAdsr,
        useGate: useGate,
        isHeld: isKeyHold,
        bleed: useBleed, // BLEED mode: play past chop boundaries
        playbackRate: transposeRate,
        onCleanup: () => {
          // Clean up UI state when playback ends. Keep the pad lit only if a
          // NEWER player owns this key (retrigger); if the map has no entry
          // (e.g. choke wiped it), this key is definitely done sounding.
          const keyOwner = activeEnvelopesRef.current[chop.key];
          if (!useGate || !keyOwner || keyOwner === sampleRef) {
            setActivePads(prev => {
              const newSet = new Set(prev);
              newSet.delete(chop.key);
              return newSet;
            });
          }
          // Stop the mini seeker (unless a newer trigger replaced this one)
          if (chopPlaybackRef.current[chop.key] === playEntry) {
            delete chopPlaybackRef.current[chop.key];
          }
          // Freeze the video for this trigger (no-op if a newer chop took over)
          if (videoVisualizerRef.current?.stopSegment) {
            videoVisualizerRef.current.stopSegment(layer.id, segSeq);
          }
          // Remove from tracking - ONLY if this player is still the tracked one
          // (a newer player may have replaced us in the map)
          if (useGate) {
            if (activeEnvelopesRef.current[chop.key] === sampleRef) {
              console.log(`[onCleanup] Removing ${chop.key} from map (this player is still active)`);
              delete activeEnvelopesRef.current[chop.key];
            } else {
              console.log(`[onCleanup] NOT removing ${chop.key} - map has different player`);
            }
          } else {
            activePlayersRef.current = activePlayersRef.current.filter(r => r !== sampleRef);
          }
        }
      });

      // Track for choke mode and gate release
      if (useGate) {
        activeEnvelopesRef.current[chop.key] = sampleRef;
        console.log(`[playChop] Added to activeEnvelopesRef for key: ${chop.key}`);
      } else {
        activePlayersRef.current.push(sampleRef);
        console.log(`[playChop] Added to activePlayersRef, new count: ${activePlayersRef.current.length}`);
      }

      setActivePads(prev => new Set(prev).add(chop.key));
    } catch (error) {
      console.error('[App] Error playing chop:', error);
    }
  };

  const releaseChop = (key) => {
    console.log(`[App] releaseChop called for key: ${key} at Tone.now(): ${Tone.now().toFixed(2)}`);

    const sampleRef = activeEnvelopesRef.current[key];
    if (!sampleRef) {
      console.log(`[App] No active sample for key: ${key} - map keys: [${Object.keys(activeEnvelopesRef.current).join(', ')}]`);
      return;
    }

    // Use the unified release method
    console.log(`[App] Calling release() on sampleRef for key: ${key}`);
    sampleRef.release();
    console.log(`[App] Release triggered for key: ${key}`);

    // DON'T delete immediately - let orphan-stopping code handle cleanup on next keydown
    // This prevents the race condition where rapid key presses create orphaned players
    // The entry will be overwritten on next keydown for this key

    // UI cleanup is handled by the onCleanup callback in triggerSample
  };

  const startLoopRecording = async () => {
    await Tone.start();
    console.log('[App] Entering listening mode (spacebar pressed)');

    // Enter listening mode - actual recording starts on first key press
    loopStartTimeRef.current = null;
    recordedEventsRef.current = [];
    setIsLoopRecording(true);
  };

  const stopLoopRecording = () => {
    // If in listening mode but no recording started yet, just exit
    if (!loopStartTimeRef.current) {
      console.log('[App] Exiting listening mode (no recording started)');
      setIsLoopRecording(false);
      return;
    }

    // Finalize any keys still being held
    Object.keys(activeRecordingKeysRef.current).forEach(key => {
      const { eventIndex, startTime } = activeRecordingKeysRef.current[key];
      const duration = Tone.now() - startTime;
      if (recordedEventsRef.current[eventIndex]) {
        recordedEventsRef.current[eventIndex].duration = duration;
        console.log('[App] Finalized pending key on stop:', key, 'duration:', duration);
      }
    });
    activeRecordingKeysRef.current = {};

    const endTime = Tone.now();
    const recordedDuration = endTime - loopStartTimeRef.current;
    const events = recordedEventsRef.current;

    console.log('[App] Stopping loop recording. Recorded duration:', recordedDuration, 'Events:', events.length);

    // If this is the first loop, set it as the master duration
    if (loopDuration === 0) {
      setLoopDuration(recordedDuration);
      setIsLoopRecording(false);
      updateActiveLayer({ recordedEvents: events, loopMultiplier: 1 });
      loopStartTimeRef.current = null;

      if (events.length > 0) {
        setTimeout(() => {
          setIsLoopPlaying(false);
          setTimeout(() => {
            playLoop(events, recordedDuration);
          }, 10);
        }, 10);
      }
      return;
    }

    // For subsequent layers, auto-detect the multiplier
    const multipliers = [0.25, 0.5, 1, 2, 4, 8, 16];
    let detectedMultiplier = 1;

    for (let mult of multipliers) {
      if (recordedDuration <= loopDuration * mult) {
        detectedMultiplier = mult;
        break;
      }
    }

    // If recording went beyond 16x, cap at 16x
    if (recordedDuration > loopDuration * 16) {
      detectedMultiplier = 16;
    }

    console.log('[App] Auto-detected multiplier:', detectedMultiplier, 'from recorded duration:', recordedDuration, 'vs master:', loopDuration);

    setIsLoopRecording(false);
    updateActiveLayer({ recordedEvents: events, loopMultiplier: detectedMultiplier });
    loopStartTimeRef.current = null;

    // Trigger playback flag - will be picked up by effect
    if (events.length > 0) {
      setTimeout(() => {
        setIsLoopPlaying(false);
        setTimeout(() => {
          playLoop(events, loopDuration);
        }, 10);
      }, 10);
    }
  };

  const recordChopEvent = (chopId, key) => {
    if (!isLoopRecording || !loopStartTimeRef.current) return;

    const eventTime = Tone.now() - loopStartTimeRef.current;
    console.log('[App] Recording chop event:', chopId, 'at time:', eventTime);

    // If choke is enabled, close ALL previously open notes (choke cuts them off)
    if (chokeEnabledRef.current) {
      Object.keys(activeRecordingKeysRef.current).forEach(openKey => {
        const { eventIndex, startTime } = activeRecordingKeysRef.current[openKey];
        const duration = Math.max(0.05, Tone.now() - startTime);
        if (recordedEventsRef.current[eventIndex]) {
          recordedEventsRef.current[eventIndex].duration = duration;
          console.log('[App] Choke closed note:', openKey, 'duration:', duration);
        }
        delete activeRecordingKeysRef.current[openKey];
      });
    }

    // Get the chop's full duration for non-gate mode
    const chopsWithEnds = getChopsWithEnds();
    const chop = chopsWithEnds.find(c => c.id === chopId);
    const chopDuration = chop ? (chop.end - chop.start) : null;

    // Determine initial duration based on mode:
    // - Gate mode: duration will be set on keyup (null for now)
    // - Choke mode (no gate): duration will be set when next note chokes it (null for now)
    // - No choke, no gate: use full chop duration immediately
    const initialDuration = (!gateEnabledRef.current && !chokeEnabledRef.current) ? chopDuration : null;

    // Store the index and start time for duration tracking (only needed for gate/choke modes)
    if (gateEnabledRef.current || chokeEnabledRef.current) {
      const eventIndex = recordedEventsRef.current.length;
      activeRecordingKeysRef.current[key] = {
        eventIndex,
        startTime: Tone.now(),
        chopDuration
      };
    }

    const newEvent = { chopId, time: eventTime, duration: initialDuration };
    recordedEventsRef.current.push(newEvent);
  };

  // Finalize event duration when key is released
  const finalizeChopEventDuration = (key, chopId) => {
    if (!isLoopRecording || !activeRecordingKeysRef.current[key]) return;

    const { eventIndex, startTime, chopDuration } = activeRecordingKeysRef.current[key];

    let duration;
    if (gateEnabledRef.current) {
      // Gate mode: duration = how long key was held
      const rawDuration = Tone.now() - startTime;
      duration = Math.max(0.05, rawDuration);
    } else if (chokeEnabledRef.current) {
      // Choke mode (key released without being choked): use full chop duration
      // because the sample would play out fully if not interrupted
      duration = chopDuration || Math.max(0.05, Tone.now() - startTime);
    } else {
      // Should not reach here (non-gate, non-choke sets duration immediately)
      duration = chopDuration || Math.max(0.05, Tone.now() - startTime);
    }

    // Update the event with the actual duration
    if (recordedEventsRef.current[eventIndex]) {
      recordedEventsRef.current[eventIndex].duration = duration;
      console.log('[App] Finalized event duration:', duration, 'for key:', key, 'mode:', gateEnabledRef.current ? 'gate' : chokeEnabledRef.current ? 'choke' : 'normal');
    }

    delete activeRecordingKeysRef.current[key];
  };

  // Keep recordedEventsRef in sync with active layer
  useEffect(() => {
    recordedEventsRef.current = recordedEvents;
  }, [recordedEvents]);

  // No auto-stop - user controls recording length with spacebar

  const playLoop = useCallback((events, duration) => {
    console.log('[App] Starting loop playback for all layers');

    // Stop any existing loop
    if (loopPlayerRef.current) {
      // Handle both single Part and array of Parts
      const parts = Array.isArray(loopPlayerRef.current) ? loopPlayerRef.current : [loopPlayerRef.current];
      parts.forEach(part => {
        if (part) {
          try {
            part.stop(0);
            part.dispose();
          } catch (e) {
            // Part may already be stopped/disposed
            try { part.dispose(); } catch (e2) {}
          }
        }
      });
      loopPlayerRef.current = null;
    }

    // Clean up any active loop sample references
    Object.values(loopActivePlayersRef.current).forEach(sampleRef => {
      if (sampleRef && sampleRef.stop) {
        sampleRef.stop();
      }
    });
    loopActivePlayersRef.current = {};

    // Stop and reset transport BEFORE creating new parts
    Tone.Transport.stop();
    Tone.Transport.cancel();
    Tone.Transport.position = 0;

    // Use a function to get the current layers synchronously
    // This avoids side effects inside setLayers which React Strict Mode calls twice
    const currentLayers = layers;

    // Determine which layers should be audible
    const hasSolo = currentLayers.some(layer => layer.isSolo);
    const audibleLayers = currentLayers.filter(layer => {
      if (hasSolo) {
        return layer.isSolo;
      }
      return !layer.isMuted;
    });

    console.log('[App] Audible layers:', audibleLayers.map(l => l.name).join(', '));

    // Find the maximum duration among all audible layers
    let maxDuration = duration;
    audibleLayers.forEach(layer => {
      // Count both recorded events AND loop clip mode as having content
      const hasContent = layer.recordedEvents.length > 0 || layer.isLoopClipEnabled;
      if (hasContent) {
        const layerMultiplier = layer.loopMultiplier || 1;
        const layerDuration = duration * layerMultiplier;
        if (layerDuration > maxDuration) {
          maxDuration = layerDuration;
        }
      }
    });

    console.log('[App] Maximum duration across all layers:', maxDuration);

    // Configure transport loop
    Tone.Transport.loop = true;
    Tone.Transport.loopEnd = maxDuration; // Use the longest layer duration

    // Create parts for each audible layer
    const newParts = audibleLayers.map(layer => {
      if (!layer.audioBuffer) {
        console.log('[App] Skipping layer', layer.name, '- no audio');
        return null;
      }

      const layerMultiplier = layer.loopMultiplier || 1;
      const layerDuration = duration * layerMultiplier;
      const layerId = layer.id;

      // LOOP CLIP MODE: Play continuous audio segment instead of discrete events
      if (layer.isLoopClipEnabled) {
        const clipStart = layer.loopClipStart ?? 0;
        const clipEnd = layer.loopClipEnd ?? layer.audioBuffer.duration;
        const clipDuration = clipEnd - clipStart;

        console.log('[App] Setting up LOOP CLIP for layer', layer.name, 'from', clipStart, 'to', clipEnd, '(', clipDuration, 's)');

        // Calculate playback rate for time-stretching
        // If this is the first layer (sets master duration), use normal rate
        // Otherwise, stretch/compress to fit the master loop duration
        let stretchRate = 1;
        if (duration > 0 && layerDuration > 0) {
          // We want clipDuration seconds of audio to fit into layerDuration seconds
          stretchRate = clipDuration / layerDuration;
        }

        // Apply transpose on top of time-stretching
        const layerTranspose = layer.transpose ?? DEFAULT_PLAYBACK_MODES.transpose;
        const transposeRate = Math.pow(2, layerTranspose / 12);
        const playbackRate = stretchRate * transposeRate;

        console.log('[App] Loop clip playback rate:', playbackRate, '(stretch:', stretchRate, 'transpose:', transposeRate, ')');

        // Create a looping player for the audio clip
        const player = new Tone.Player({
          url: layer.audioBuffer,
          loop: true,
          loopStart: clipStart,
          loopEnd: clipEnd,
          playbackRate: playbackRate
        }).connect(getChopsGain());

        // Schedule the player to start at time 0
        player.sync().start(0);

        // Return an object that mimics Part interface for cleanup
        return {
          stop: (time) => {
            try {
              player.stop(time);
            } catch (e) {}
          },
          dispose: () => {
            try {
              player.unsync();
              player.stop();
              player.dispose();
            } catch (e) {}
          },
          _isLoopClipPlayer: true,
          _layerId: layerId
        };
      }

      // NORMAL MODE: Discrete chop events
      if (layer.recordedEvents.length === 0) {
        console.log('[App] Skipping layer', layer.name, '- no events');
        return null;
      }

      console.log('[App] Setting up playback for layer', layer.name, 'with', layer.recordedEvents.length, 'events, multiplier:', layerMultiplier, 'duration:', layerDuration);

      // Get chops with ends for this layer
      const layerChops = [...layer.chops].sort((a, b) => a.start - b.start).map((chop, index, arr) => {
        const nextChop = arr[index + 1];
        const end = nextChop ? nextChop.start : layer.audioBuffer.duration;
        return { ...chop, end };
      });

      // Get layer's ADSR settings (for envelope)
      const layerAdsr = {
        attack: layer.attack ?? DEFAULT_ADSR.attack,
        decay: layer.decay ?? DEFAULT_ADSR.decay,
        sustain: layer.sustain ?? DEFAULT_ADSR.sustain,
        release: layer.release ?? DEFAULT_ADSR.release
      };

      // Get layer's playback mode settings
      const layerChokeEnabled = layer.chokeEnabled ?? DEFAULT_PLAYBACK_MODES.chokeEnabled;
      const layerBleedEnabled = layer.bleedEnabled ?? DEFAULT_PLAYBACK_MODES.bleedEnabled;
      const layerTranspose = layer.transpose ?? DEFAULT_PLAYBACK_MODES.transpose;
      const layerTransposeRate = Math.pow(2, layerTranspose / 12);

      const part = new Tone.Part((time, event) => {
        const chop = layerChops.find(c => c.id === event.chopId);
        if (chop && layer.audioBuffer) {
          // If choke is enabled for this layer, stop the previous player
          if (layerChokeEnabled && loopActivePlayersRef.current[layerId]) {
            loopActivePlayersRef.current[layerId].stop();
          }

          const chopDuration = event.duration || (chop.end - chop.start);

          // Trigger visual flash + video seek only for the currently active layer
          Tone.Draw.schedule(() => {
            if (layerId === activeLayerIdRef.current) {
              triggerFlash(chop.key);
              if (videoVisualizerRef.current) {
                videoVisualizerRef.current.playSegment(chop.start, chopDuration, layerTransposeRate);
              }
            }
          }, time);

          // Use unified trigger function - same as live playback!
          const sampleRef = triggerSample({
            buffer: layer.audioBuffer,
            startOffset: chop.start,
            duration: chopDuration,
            bufferDuration: layer.audioBuffer.duration, // For BLEED mode
            time: time, // Scheduled time from Transport
            adsr: layerAdsr,
            useGate: false, // Loop playback uses one-shot mode with micro-fades
            isHeld: false,
            bleed: layerBleedEnabled, // BLEED mode: play past chop boundaries
            playbackRate: layerTransposeRate,
            onCleanup: () => {
              if (loopActivePlayersRef.current[layerId] === sampleRef) {
                loopActivePlayersRef.current[layerId] = null;
              }
            }
          });

          // Track this sample for choke
          loopActivePlayersRef.current[layerId] = sampleRef;
        }
      }, layer.recordedEvents.map(e => [e.time, e]));

      part.loop = true;
      part.loopEnd = layerDuration;
      part.start(0);

      return part;
    }).filter(p => p !== null);

    loopPlayerRef.current = newParts;
    setIsLoopPlaying(true);

    Tone.Transport.start();

    console.log('[App] Loop started with', newParts.length, 'layer parts');
  }, [layers, triggerSample, triggerFlash]);

  // Track mute/solo state to detect changes
  const muteStateRef = useRef(layers.map(l => l.isMuted).join(','));
  const soloStateRef = useRef(layers.map(l => l.isSolo).join(','));

  // Restart playback when mute/solo changes (but not on every layer change)
  useEffect(() => {
    const currentMuteState = layers.map(l => l.isMuted).join(',');
    const currentSoloState = layers.map(l => l.isSolo).join(',');

    // Only restart if mute/solo actually changed
    if (muteStateRef.current !== currentMuteState || soloStateRef.current !== currentSoloState) {
      muteStateRef.current = currentMuteState;
      soloStateRef.current = currentSoloState;

      if (isLoopPlaying && loopDuration > 0) {
        console.log('[App] Mute/solo changed, restarting playback');
        setTimeout(() => {
          playLoop(recordedEventsRef.current, loopDuration);
        }, 20);
      }
    }
  }, [layers, isLoopPlaying, loopDuration, playLoop]);

  // Update loop current time for playhead visualization
  useEffect(() => {
    if (!isLoopPlaying) {
      setLoopCurrentTime(0);
      return;
    }

    // Find the maximum duration among all layers for playhead
    const maxDuration = Math.max(
      loopDuration,
      ...layers.map(layer => {
        const mult = layer.loopMultiplier || 1;
        return loopDuration * mult;
      })
    );

    const interval = setInterval(() => {
      const time = Tone.Transport.seconds % maxDuration;
      setLoopCurrentTime(time);
    }, 50); // Update every 50ms for smooth playhead

    return () => clearInterval(interval);
  }, [isLoopPlaying, loopDuration, layers]);

  const pauseLoop = () => {
    console.log('[App] Pausing loop');
    Tone.Transport.pause();

    // Stop all currently playing loop samples (important for BLEED mode)
    Object.values(loopActivePlayersRef.current).forEach(sampleRef => {
      if (sampleRef && sampleRef.stop) {
        sampleRef.stop();
      }
    });
    loopActivePlayersRef.current = {};

    setIsLoopPlaying(false);
  };

  const resumeLoop = () => {
    console.log('[App] Resuming loop');
    Tone.Transport.start();
    setIsLoopPlaying(true);
  };

  // Seek to specific time in the loop
  const seekLoop = useCallback((time) => {
    console.log('[App] Seeking to:', time);
    setLoopCurrentTime(time);

    // If loop is playing, update Transport position
    if (Tone.Transport.state === 'started') {
      Tone.Transport.seconds = time;
    }
  }, []);

  const clearLoop = () => {
    console.log('[App] Clearing current layer loop');

    // Clear the active layer's recorded events
    recordedEventsRef.current = [];
    updateActiveLayer({ recordedEvents: [] });

    // Check if any other layers have recorded events
    const otherLayersHaveEvents = layers.some(layer =>
      layer.id !== activeLayerId && layer.recordedEvents.length > 0
    );

    // If other layers have events, keep the master duration and restart playback
    if (otherLayersHaveEvents) {
      console.log('[App] Other layers have events, keeping master duration and restarting playback');

      // Restart playback with remaining layers
      if (isLoopPlaying) {
        setTimeout(() => {
          playLoop([], loopDuration);
        }, 20);
      }
    } else {
      // No other layers have events, reset everything
      console.log('[App] No other layers have events, resetting master duration');

      if (loopPlayerRef.current) {
        // Handle both single Part and array of Parts
        const parts = Array.isArray(loopPlayerRef.current) ? loopPlayerRef.current : [loopPlayerRef.current];
        parts.forEach(part => {
          if (part) {
            try {
              part.stop(0);
              part.dispose();
            } catch (e) {
              try { part.dispose(); } catch (e2) {}
            }
          }
        });
        loopPlayerRef.current = null;
      }

      Tone.Transport.stop();
      Tone.Transport.cancel();

      setLoopDuration(0);
      setIsLoopPlaying(false);
      setRecordedLoop(null);
    }
  };

  const updateLoopEvent = useCallback((index, updatedEvent) => {
    console.log('[App] Updating event at index', index, 'to', updatedEvent);

    // Validate the updated event
    if (!updatedEvent || updatedEvent.time === undefined || !updatedEvent.chopId) {
      console.error('[App] Invalid event update:', updatedEvent);
      return;
    }

    const newEvents = [...recordedEventsRef.current];
    newEvents[index] = updatedEvent;

    // Filter out any invalid events
    const validEvents = newEvents.filter(e => e && e.time !== undefined && e.chopId);

    recordedEventsRef.current = validEvents;
    updateActiveLayer({ recordedEvents: validEvents });

    // If loop is playing, restart it with updated events
    if (isLoopPlaying) {
      setTimeout(() => playLoop(validEvents, loopDuration), 20);
    }
  }, [isLoopPlaying, loopDuration, updateActiveLayer, playLoop]);

  const updateLoopDuration = useCallback((newDuration) => {
    console.log('[App] Updating loop duration to', newDuration);
    setLoopDuration(newDuration);

    // If loop is playing, restart it with new duration
    if (isLoopPlaying) {
      setTimeout(() => playLoop(recordedEventsRef.current, newDuration), 20);
    }
  }, [isLoopPlaying, playLoop]);

  // Delete loop events by indices
  const deleteLoopEvents = useCallback((indicesToDelete) => {
    console.log('[App] Deleting events at indices:', indicesToDelete);

    const sortedIndices = [...indicesToDelete].sort((a, b) => b - a); // Delete from end to preserve indices
    const newEvents = [...recordedEventsRef.current];

    sortedIndices.forEach(index => {
      if (index >= 0 && index < newEvents.length) {
        newEvents.splice(index, 1);
      }
    });

    recordedEventsRef.current = newEvents;
    updateActiveLayer({ recordedEvents: newEvents });

    // If loop is playing, restart it with updated events
    if (isLoopPlaying) {
      setTimeout(() => playLoop(newEvents, loopDuration), 20);
    }
  }, [isLoopPlaying, loopDuration, updateActiveLayer, playLoop]);

  // Add new loop events
  const addLoopEvents = useCallback((newEvents) => {
    console.log('[App] Adding events:', newEvents);

    const updatedEvents = [...recordedEventsRef.current, ...newEvents];
    recordedEventsRef.current = updatedEvents;
    updateActiveLayer({ recordedEvents: updatedEvents });

    // If loop is playing, restart it with updated events
    if (isLoopPlaying) {
      setTimeout(() => playLoop(updatedEvents, loopDuration), 20);
    }
  }, [isLoopPlaying, loopDuration, updateActiveLayer, playLoop]);

  // Layer management functions
  const addLayer = () => {
    const newId = Math.max(...layers.map(l => l.id), 0) + 1;
    const newColorIndex = layers.length; // Next color in palette (cycles automatically)
    const newLayer = {
      id: newId,
      name: `Layer ${newId}`,
      colorIndex: newColorIndex,
      audioFile: null,
      audioBuffer: null,
      videoUrl: null,
      youtubeTitle: null,
      chops: [],
      recordedEvents: [],
      isMuted: false,
      isSolo: false,
      loopMultiplier: 1,
      ...DEFAULT_ADSR, // Per-layer ADSR defaults
      ...DEFAULT_PLAYBACK_MODES, // Per-layer playback mode defaults
      ...DEFAULT_LOOP_CLIP // Per-layer loop clip defaults
    };
    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newId);
    console.log('[App] Added new layer:', newId, 'with color index:', newColorIndex);
  };

  // Duplicate the active layer
  const duplicateLayer = useCallback(() => {
    const sourceLayer = getActiveLayer();
    if (!sourceLayer) return;

    const newId = Math.max(...layers.map(l => l.id), 0) + 1;
    const newColorIndex = layers.length;

    // Deep copy chops with new IDs
    const newChops = sourceLayer.chops.map((chop, index) => ({
      ...chop,
      id: Date.now() + index
    }));

    // Deep copy recorded events, mapping to new chop IDs
    const chopIdMap = {};
    sourceLayer.chops.forEach((oldChop, index) => {
      chopIdMap[oldChop.id] = newChops[index]?.id;
    });

    const newRecordedEvents = sourceLayer.recordedEvents.map(event => ({
      ...event,
      chopId: chopIdMap[event.chopId] || event.chopId
    }));

    const newLayer = {
      id: newId,
      name: `${sourceLayer.name} (copy)`,
      colorIndex: newColorIndex,
      audioFile: sourceLayer.audioFile,
      audioBuffer: sourceLayer.audioBuffer,
      chops: newChops,
      recordedEvents: newRecordedEvents,
      isMuted: false,
      isSolo: false,
      loopMultiplier: sourceLayer.loopMultiplier,
      // Copy ADSR settings from source layer
      attack: sourceLayer.attack ?? DEFAULT_ADSR.attack,
      decay: sourceLayer.decay ?? DEFAULT_ADSR.decay,
      sustain: sourceLayer.sustain ?? DEFAULT_ADSR.sustain,
      release: sourceLayer.release ?? DEFAULT_ADSR.release,
      // Copy playback mode settings from source layer
      gateEnabled: sourceLayer.gateEnabled ?? DEFAULT_PLAYBACK_MODES.gateEnabled,
      chokeEnabled: sourceLayer.chokeEnabled ?? DEFAULT_PLAYBACK_MODES.chokeEnabled,
      bleedEnabled: sourceLayer.bleedEnabled ?? DEFAULT_PLAYBACK_MODES.bleedEnabled,
      // Copy loop clip settings from source layer
      isLoopClipEnabled: sourceLayer.isLoopClipEnabled ?? DEFAULT_LOOP_CLIP.isLoopClipEnabled,
      loopClipStart: sourceLayer.loopClipStart ?? DEFAULT_LOOP_CLIP.loopClipStart,
      loopClipEnd: sourceLayer.loopClipEnd ?? DEFAULT_LOOP_CLIP.loopClipEnd
    };

    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newId);
    console.log('[App] Duplicated layer:', sourceLayer.id, 'to new layer:', newId);
  }, [layers, getActiveLayer]);

  // Delete the active layer
  const deleteLayer = useCallback(() => {
    const sourceLayer = getActiveLayer();
    if (!sourceLayer) return;

    // Don't delete if it's the only layer
    if (layers.length <= 1) {
      console.log('[App] Cannot delete the only layer');
      return;
    }

    // Check if confirmation is needed
    const hasContent = sourceLayer.chops.length > 0 || sourceLayer.recordedEvents.length > 0;
    if (hasContent) {
      const confirmed = window.confirm(
        `Delete "${sourceLayer.name}"?\n\nThis clip has ${sourceLayer.chops.length} chop(s) and ${sourceLayer.recordedEvents.length} recorded note(s). This action cannot be undone.`
      );
      if (!confirmed) return;
    }

    // Find the next layer to select
    const currentIndex = layers.findIndex(l => l.id === activeLayerId);
    const nextLayer = layers[currentIndex - 1] || layers[currentIndex + 1];

    setLayers(prev => prev.filter(l => l.id !== activeLayerId));
    if (nextLayer) {
      setActiveLayerId(nextLayer.id);
    }

    console.log('[App] Deleted layer:', sourceLayer.id);
  }, [layers, activeLayerId, getActiveLayer]);

  const selectLayer = (layerId) => {
    setActiveLayerId(layerId);
    console.log('[App] Selected layer:', layerId);
  };

  const toggleMute = (layerId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, isMuted: !layer.isMuted } : layer
    ));
    console.log('[App] Toggled mute for layer:', layerId);
  };

  const toggleSolo = (layerId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, isSolo: !layer.isSolo } : layer
    ));
    console.log('[App] Toggled solo for layer:', layerId);
  };

  const setLoopMultiplier = (multiplier) => {
    updateActiveLayer({ loopMultiplier: multiplier });
    console.log('[App] Set loop multiplier to', multiplier, 'for active layer');
  };

  // Load a saved project - attempts to auto-reload audio files from stored handles
  const loadProject = useCallback(async (project) => {
    console.log('[App] Loading project:', project.name);

    // Stop any playing loop
    if (loopPlayerRef.current) {
      const parts = Array.isArray(loopPlayerRef.current) ? loopPlayerRef.current : [loopPlayerRef.current];
      parts.forEach(part => {
        if (part) {
          try {
            part.stop(0);
            part.dispose();
          } catch (e) {}
        }
      });
      loopPlayerRef.current = null;
    }
    Tone.Transport.stop();
    Tone.Transport.cancel();
    setIsLoopPlaying(false);

    // Start Tone context for audio decoding
    await Tone.start();
    Tone.context.lookAhead = 0;

    // Track which layers need manual reload
    const failedLayers = [];
    const loadedLayers = [];

    // Attempt to restore layers with auto-reload from file handles
    const restoredLayers = await Promise.all(project.layers.map(async (layer) => {
      const baseLayer = {
        id: layer.id,
        name: layer.name,
        colorIndex: layer.colorIndex,
        audioFile: null,
        audioBuffer: null,
        chops: layer.chops || [],
        recordedEvents: layer.recordedEvents || [],
        isMuted: layer.isMuted || false,
        isSolo: layer.isSolo || false,
        loopMultiplier: layer.loopMultiplier || 1,
        // Restore ADSR settings
        attack: layer.attack ?? DEFAULT_ADSR.attack,
        decay: layer.decay ?? DEFAULT_ADSR.decay,
        sustain: layer.sustain ?? DEFAULT_ADSR.sustain,
        release: layer.release ?? DEFAULT_ADSR.release,
        // Restore playback mode settings
        gateEnabled: layer.gateEnabled ?? DEFAULT_PLAYBACK_MODES.gateEnabled,
        chokeEnabled: layer.chokeEnabled ?? DEFAULT_PLAYBACK_MODES.chokeEnabled,
        bleedEnabled: layer.bleedEnabled ?? DEFAULT_PLAYBACK_MODES.bleedEnabled,
        transpose: layer.transpose ?? DEFAULT_PLAYBACK_MODES.transpose,
        // Restore loop clip settings
        isLoopClipEnabled: layer.isLoopClipEnabled ?? DEFAULT_LOOP_CLIP.isLoopClipEnabled,
        loopClipStart: layer.loopClipStart ?? DEFAULT_LOOP_CLIP.loopClipStart,
        loopClipEnd: layer.loopClipEnd ?? DEFAULT_LOOP_CLIP.loopClipEnd,
        // Store reference info
        expectedFileName: layer.audioFileName,
        fileHandleId: layer.fileHandleId
      };

      // Try to auto-reload from stored file handle
      if (layer.fileHandleId) {
        try {
          console.log('[App] Attempting to reload file for layer:', layer.name, 'handle:', layer.fileHandleId);
          const stored = await getFileHandle(layer.fileHandleId);

          if (stored && stored.handle) {
            // Request permission and get file
            const file = await requestFileAccess(stored.handle);

            if (file) {
              // Decode audio
              const arrayBuffer = await file.arrayBuffer();
              const buffer = await Tone.context.decodeAudioData(arrayBuffer);

              console.log('[App] Successfully reloaded audio for layer:', layer.name);
              loadedLayers.push(layer.name);

              return {
                ...baseLayer,
                audioFile: file,
                audioBuffer: buffer
              };
            }
          }
        } catch (error) {
          console.warn('[App] Failed to reload file for layer:', layer.name, error);
        }
      }

      // If we get here, auto-reload failed
      if (layer.audioFileName) {
        failedLayers.push({ name: layer.name, fileName: layer.audioFileName });
      }

      return baseLayer;
    }));

    setLayers(restoredLayers);
    setActiveLayerId(project.activeLayerId || restoredLayers[0]?.id || 1);
    setLoopDuration(project.loopDuration || 0);

    // Show feedback about what was loaded
    setTimeout(() => {
      if (loadedLayers.length > 0 && failedLayers.length === 0) {
        // All files loaded successfully
        alert(`Project loaded!\n\nAll ${loadedLayers.length} audio file(s) were automatically reloaded.`);
      } else if (failedLayers.length > 0) {
        // Some or all files need manual reload
        const loadedMsg = loadedLayers.length > 0
          ? `\n\nAutomatically loaded:\n${loadedLayers.map(n => `✓ ${n}`).join('\n')}\n`
          : '';
        const failedMsg = `\nNeed manual re-upload:\n${failedLayers.map(l => `• ${l.name}: ${l.fileName}`).join('\n')}`;

        alert(`Project loaded!${loadedMsg}${failedMsg}\n\nTip: Use the file picker button to upload files - this enables auto-reload for future sessions.`);
      }
    }, 100);

    console.log('[App] Project loaded with', restoredLayers.length, 'layers,', loadedLayers.length, 'auto-reloaded');
  }, []);

  // Smart autochop: detect transients and create chops automatically
  const handleAutoChop = async (sensitivity, frequencyFocus) => {
    if (!audioBuffer) {
      console.log('[App] No audio buffer for autochop');
      return;
    }

    console.log('[App] Running smart autochop with sensitivity:', sensitivity, 'frequency focus:', frequencyFocus);

    try {
      const chopPoints = await smartChop(audioBuffer, sensitivity, frequencyFocus, AVAILABLE_KEYS.length);
      console.log('[App] Smart chop found', chopPoints.length, 'chop points');

      // Create chops from the detected points, assigning keys in order
      const newChops = chopPoints.map((point, index) => ({
        id: Date.now() + index,
        key: AVAILABLE_KEYS[index],
        start: point.timestamp
      }));

      // Update the active layer with new chops (replacing any existing)
      updateActiveLayer({ chops: newChops });
      console.log('[App] Smart chop complete, created', newChops.length, 'chops');
    } catch (error) {
      console.error('[App] Smart chop error:', error);
    }
  };

  // Interval chop: divide sample into equal parts
  const handleIntervalChop = (divisions) => {
    if (!audioBuffer) {
      console.log('[App] No audio buffer for interval chop');
      return;
    }

    console.log('[App] Running interval chop with', divisions, 'divisions');

    const chopPoints = intervalChop(audioBuffer, divisions);

    // Create chops from the interval points, assigning keys in order
    const newChops = chopPoints.map((point, index) => ({
      id: Date.now() + index,
      key: AVAILABLE_KEYS[index % AVAILABLE_KEYS.length],
      start: point.timestamp
    }));

    // Update the active layer with new chops (replacing any existing)
    updateActiveLayer({ chops: newChops });
    console.log('[App] Interval chop complete, created', newChops.length, 'chops');
  };

  const exportLoop = async () => {
    if (!loopDuration) {
      console.log('[App] No loop duration set');
      return;
    }

    console.log('[App] Exporting all layers separately');

    // Export each layer that has content (recorded events OR loop clip mode)
    const layersToExport = layers.filter(layer =>
      layer.audioBuffer && (layer.recordedEvents.length > 0 || layer.isLoopClipEnabled)
    );

    if (layersToExport.length === 0) {
      console.log('[App] No layers to export');
      return;
    }

    for (const layer of layersToExport) {
      console.log('[App] Exporting layer:', layer.name, 'with multiplier:', layer.loopMultiplier);

      // Calculate the actual loop duration for this layer (accounting for multiplier)
      const layerMultiplier = layer.loopMultiplier || 1;
      const layerLoopDuration = loopDuration * layerMultiplier;

      console.log('[App] Layer loop duration:', layerLoopDuration);

      // Get layer settings
      const layerBleedEnabled = layer.bleedEnabled ?? false;
      const layerChokeEnabled = layer.chokeEnabled ?? false;
      const layerTranspose = layer.transpose ?? 0;
      const transposeRate = Math.pow(2, layerTranspose / 12);

      const buffer = await Tone.Offline(({ transport }) => {
        // LOOP CLIP MODE: Continuous audio segment
        if (layer.isLoopClipEnabled) {
          const clipStart = layer.loopClipStart ?? 0;
          const clipEnd = layer.loopClipEnd ?? layer.audioBuffer.duration;
          const clipDuration = clipEnd - clipStart;

          // Calculate playback rate for time-stretching
          let stretchRate = 1;
          if (loopDuration > 0 && layerLoopDuration > 0) {
            stretchRate = clipDuration / layerLoopDuration;
          }

          // Apply transpose on top of time-stretching
          const playbackRate = stretchRate * transposeRate;

          console.log('[App] Exporting LOOP CLIP for layer', layer.name, 'rate:', playbackRate);

          const player = new Tone.Player(layer.audioBuffer).toDestination();
          player.playbackRate = playbackRate;
          player.start(0, clipStart, clipDuration);
        } else {
          // NORMAL MODE: Discrete chop events
          // Get chops with ends for this layer
          const layerChops = [...layer.chops].sort((a, b) => a.start - b.start).map((chop, index, arr) => {
            const nextChop = arr[index + 1];
            const end = nextChop ? nextChop.start : layer.audioBuffer.duration;
            return { ...chop, end };
          });

          // Build list of events for single loop iteration
          const allEvents = layer.recordedEvents.map(event => {
            const chop = layerChops.find(c => c.id === event.chopId);
            if (chop) {
              return {
                startTime: event.time,
                chopStart: chop.start,
                chopEnd: chop.end,
                duration: event.duration || (chop.end - chop.start)
              };
            }
            return null;
          }).filter(Boolean);

          // Sort by start time
          allEvents.sort((a, b) => a.startTime - b.startTime);

          // Schedule events, handling choke (each new event stops the previous)
          let previousPlayer = null;

          allEvents.forEach((event) => {
            const player = new Tone.Player(layer.audioBuffer).toDestination();
            player.playbackRate = transposeRate;

            // Calculate when this event should end
            let effectiveDuration;
            if (layerBleedEnabled) {
              // BLEED: play to end of buffer, BUT if choke is on, we'll be stopped early
              effectiveDuration = layer.audioBuffer.duration - event.chopStart;
            } else {
              effectiveDuration = event.duration;
            }

            // If choke is enabled, stop previous player when this one starts
            // Adjust timing for playback rate
            if (layerChokeEnabled && previousPlayer) {
              previousPlayer.stop(event.startTime);
            }

            player.start(event.startTime, event.chopStart, effectiveDuration);
            previousPlayer = player;
          });
        }

        // Start transport
        transport.start();
      }, layerLoopDuration);

      console.log('[App] Layer', layer.name, 'render complete');

      const wav = bufferToWave(buffer, buffer.length);
      const blob = new Blob([wav], { type: 'audio/wav' });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chop-loop-${layer.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.wav`;
      a.click();
      URL.revokeObjectURL(url);

      console.log('[App]', layer.name, 'exported');
    }
  };

  // Helper function to convert AudioBuffer to WAV
  const bufferToWave = (abuffer, len) => {
    const numOfChan = abuffer.numberOfChannels;
    const length = len * numOfChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);
    const channels = [];
    let offset = 0;
    let pos = 0;

    // Write WAV header
    const setUint16 = (data) => { view.setUint16(pos, data, true); pos += 2; };
    const setUint32 = (data) => { view.setUint32(pos, data, true); pos += 4; };

    setUint32(0x46464952); // "RIFF"
    setUint32(length - 8); // file length - 8
    setUint32(0x45564157); // "WAVE"
    setUint32(0x20746d66); // "fmt " chunk
    setUint32(16); // length = 16
    setUint16(1); // PCM (uncompressed)
    setUint16(numOfChan);
    setUint32(abuffer.sampleRate);
    setUint32(abuffer.sampleRate * 2 * numOfChan); // avg. bytes/sec
    setUint16(numOfChan * 2); // block-align
    setUint16(16); // 16-bit
    setUint32(0x61746164); // "data" - chunk
    setUint32(length - pos - 4); // chunk length

    // Write interleaved data
    for (let i = 0; i < abuffer.numberOfChannels; i++) {
      channels.push(abuffer.getChannelData(i));
    }

    while (pos < length) {
      for (let i = 0; i < numOfChan; i++) {
        let sample = Math.max(-1, Math.min(1, channels[i][offset]));
        sample = (sample < 0 ? sample * 0x8000 : sample * 0x7FFF) | 0;
        view.setInt16(pos, sample, true);
        pos += 2;
      }
      offset++;
    }

    return buffer;
  };

  // Export all layer audio samples as separate WAV files
  const exportAllSamples = async () => {
    const layersWithAudio = layers.filter(layer => layer.audioBuffer);

    if (layersWithAudio.length === 0) {
      console.log('[App] No layers with audio to export');
      return;
    }

    console.log('[App] Exporting', layersWithAudio.length, 'audio samples');

    for (const layer of layersWithAudio) {
      try {
        // Convert the AudioBuffer to WAV
        const wav = bufferToWave(layer.audioBuffer, layer.audioBuffer.length);
        const blob = new Blob([wav], { type: 'audio/wav' });

        // Generate filename from layer name or original file
        const baseName = layer.audioFile?.name?.replace(/\.[^/.]+$/, '') || layer.name;
        const filename = `${baseName.toLowerCase().replace(/\s+/g, '-')}-${layer.id}.wav`;

        // Download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        console.log('[App] Exported:', filename);

        // Small delay between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('[App] Error exporting layer', layer.name, ':', error);
      }
    }

    console.log('[App] All samples exported');
  };

  // Keyboard handler for real-time chopping during playback and key-hold GATE
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.repeat) return; // Ignore key repeat events

      // Don't steal keys while the user is typing (e.g. the YouTube link field)
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      const key = e.key.toUpperCase();

      // Handle spacebar: source playback while chopping, loop transport once a loop exists
      if (e.code === 'Space') {
        e.preventDefault();
        if (loopDuration === 0) {
          wavesurferRef.current?.playPause?.();
          return;
        }
        // Toggle loop playback
        if (isLoopPlaying) {
          pauseLoop();
        } else {
          playLoop(recordedEvents, loopDuration);
        }
        return;
      }

      // Handle Enter for loop recording
      if (e.code === 'Enter') {
        e.preventDefault();
        // Toggle recording mode (enter listening, or stop if already recording)
        if (!isLoopRecording) {
          startLoopRecording();
        } else {
          stopLoopRecording();
        }
        return;
      }

      // Handle Cmd+D for duplicate layer
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        duplicateLayer();
        return;
      }

      // Check if it's a valid chop key
      if (!AVAILABLE_KEYS.includes(key)) return;

      // If playing, add chop at current time — but a key already owned by
      // ANOTHER source is blocked (the keyboard is shared across sources)
      if (isPlaying && wavesurferRef.current) {
        const owner = chopKeyOwnersRef.current[key];
        if (owner != null && owner !== activeLayerId) {
          console.log('[App] Key', key, 'belongs to source', owner, '— blocked');
          return;
        }
        const time = wavesurferRef.current.getCurrentTime();
        addChopAtTime(key, time);
        console.log('[App] Chop added at', time, 'for key', key);
        return;
      }

      // If not playing, try to play an existing chop from ANY source
      let chop = null;
      for (const layer of layers) {
        const found = computeChopsWithEnds(layer).find(c => c.key === key);
        if (found) { chop = found; break; }
      }

      if (chop) {
        console.log(`[KEYDOWN] key=${key} at Tone.now()=${Tone.now().toFixed(2)}, gateEnabled=${gateEnabled}`);

        // If in listening mode (recording but not started), start actual recording now
        if (isLoopRecording && !loopStartTimeRef.current) {
          loopStartTimeRef.current = Tone.now();
          console.log('[App] Started actual recording on first key press');
        }

        // Record the event if we're loop recording
        if (isLoopRecording && loopStartTimeRef.current) {
          recordChopEvent(chop.id, key);
        }

        // Pass isKeyHold=true for GATE envelope behavior
        playChop(chop, true);
      }
    };

    const handleKeyUp = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      const key = e.key.toUpperCase();

      // Check if it's a valid chop key
      if (!AVAILABLE_KEYS.includes(key)) return;

      console.log(`[KEYUP] key=${key} at Tone.now()=${Tone.now().toFixed(2)}, gateEnabled=${gateEnabled}, inMap=${!!activeEnvelopesRef.current[key]}`);

      // Finalize event duration if we're recording
      if (isLoopRecording && activeRecordingKeysRef.current[key]) {
        const chopsWithEnds = getChopsWithEnds();
        const chop = chopsWithEnds.find(c => c.key === key);
        if (chop) {
          finalizeChopEventDuration(key, chop.id);
        }
      }

      // Release if a gated envelope is sounding for this key. The envelope
      // only exists if the OWNING layer had gate on, so no per-layer check
      // is needed here (the focused layer's gate setting is irrelevant).
      if (activeEnvelopesRef.current[key]) {
        releaseChop(key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isPlaying, audioBuffer, gateEnabled, isLoopRecording, addChopAtTime, getChopsWithEnds, duplicateLayer, loopDuration, isLoopPlaying, layers, activeLayerId]);

  if (view === 'slides') {
    return (
      <SlideDeck
        onExit={() => {
          try { window.history.replaceState(null, '', window.location.pathname); } catch { /* noop */ }
          setView('main');
        }}
      />
    );
  }

  if (view === 'loop-editor' && drumsUrl) {
    return (
      <div className="app-shell">
        <DrumLoopEditor
          audioUrl={drumsUrl}
          fileName={drums?.name}
          loop={drumLoopPoints}
          onSave={saveDrumLoopPoints}
          onBack={() => setView('main')}
          onReplace={handleAddDrums}
        />
      </div>
    );
  }

  const sources = layers
    .filter(l => l.audioBuffer)
    .map(l => ({
      id: l.id,
      audioBuffer: l.audioBuffer,
      audioFile: l.audioFile,
      videoUrl: l.videoUrl,
      title: l.youtubeTitle || l.audioFile?.name,
      chops: computeChopsWithEnds(l)
    }));
  const hasAnyAudio = sources.length > 0;

  // If the drum loop came from a lane chop (right-click → loop), light that
  // chop up and sweep its cell with the loop position while the drums play.
  let drumLoopVisual = null;
  if (drumLoopPlaying && drums?.sourceChopId != null) {
    const srcChop = sources.flatMap(s => s.chops).find(c => c.id === drums.sourceChopId);
    if (srcChop) {
      const anchor = drums.sourceChopStart ?? srcChop.start;
      const offsetStart = anchor + (drumLoopPoints?.start || 0);
      const offsetEnd = drumLoopPoints?.end != null ? anchor + drumLoopPoints.end : srcChop.end;
      drumLoopVisual = {
        chopId: srcChop.id,
        startedAt: drumLoopStartedAtRef.current,
        offsetStart,
        offsetEnd: Math.max(offsetEnd, offsetStart + 0.05)
      };
    }
  }

  return (
    <>
      <div className="app-shell">
        {!hasAnyAudio ? (
          <Landing
            onFileUpload={handleFileUpload}
            onYouTubeSample={handleYouTubeSample}
          />
        ) : (
          <ChopStation
            sources={sources}
            focusedId={activeLayerId}
            onFocusSource={selectLayer}
            onAddSourceFile={handleAddSourceFile}
            onAddSourceUrl={handleAddSourceUrl}
            flashingChops={flashingChops}
            activeChops={activePads}
            chopPlaybackRef={chopPlaybackRef}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            transportRef={wavesurferRef}
            videoVisualizerRef={videoVisualizerRef}
            onChopClick={(chop) => playChop(chop, false, { bleedOverride: false })}
            onUpdateChopStart={updateChopStart}
            onUpdateChopEnd={updateChopEnd}
            onRemoveChop={removeChop}
            onLoopChop={handleLoopChopAsDrums}
            drumLoopVisual={drumLoopVisual}
            onSourcePause={finalizeOpenChop}
            samplerOptions={[
              { key: 'gate', label: 'gate', value: gateEnabled, onChange: setGateEnabled, hint: 'chops sustain while the key is held, release to stop' },
              { key: 'choke', label: 'choke', value: chokeEnabled, onChange: setChokeEnabled, hint: 'a new chop cuts off the previous one' },
              { key: 'bleed', label: 'bleed', value: bleedEnabled, onChange: setBleedEnabled, hint: 'chops play past their end point until released or choked' }
            ]}
            transpose={{ value: transpose, onChange: setTranspose }}
            drumLoop={{
              hasDrums: !!drums,
              playing: drumLoopPlaying,
              onToggle: toggleDrumLoop,
              onEdit: () => { setIsPlaying(false); setView('loop-editor'); },
              onAdd: handleAddDrums,
              onUseDefault: handleUseDefaultDrums
            }}
          />
        )}
        <ProjectsPanel
          activeId={activeProject?.id || null}
          activeName={activeProject?.name || null}
          canSaveCurrent={hasAnyAudio}
          onLoad={handleLoadSavedProject}
          onNew={handleNewProject}
          onSaveCurrent={handleSaveCurrentProject}
          onDeleted={handleProjectDeleted}
        />
        <Mixer
          channels={[
            { key: 'chops', label: 'chops', value: mixerLevels.chops, onChange: (db) => setMixerLevels((l) => ({ ...l, chops: db })) },
            { key: 'drums', label: 'drums', value: mixerLevels.drums, onChange: (db) => setMixerLevels((l) => ({ ...l, drums: db })) }
          ]}
        />
      </div>
      {process.env.NODE_ENV === 'development' && <Agentation />}
    </>
  );
}
export default App;
